import { useSelector } from "react-redux";
import { usePayerNameContextFromSingleOrderLayout } from "../../context/payerNameContextFromSingleOrderLayout";
import { BadgeWithIconProps } from "../../data-model/badgeWithIconProps";
import { ScreenNamesForBigQuery } from "../../enums/eventsData";
import { EventsName } from "../../enums/eventsName";
import { LocalStorageKeys } from "../../enums/localStorageKeys";
import { RootState } from "../../redux/store/store";
import { getItemFromLocalStorage } from "../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { apiWorkerManager } from "../../workerManagers/workerManager";
import BadgeDropdown from "../badgeDropdown/badgeDropdown";

interface SelectableBadgeProps {
  selectedBadge: BadgeWithIconProps | null;
  badgeList: BadgeWithIconProps[];
  onClick: (id: string) => void;
  onValueChange: (value: string) => void;
  rowIndex?: number;
  fieldKey?: string;
  onTableRowUpdate?: (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => void;
  shouldUsePortal?: boolean;
  onDefaultBadgeClick?: (id: string) => void;
}

const SelectableBadge = (props: SelectableBadgeProps) => {
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const handleBadgeSelection = (badgeId: string) => {
    const selectedBadge = props.badgeList.find((badge) => badge.id === badgeId);

    if (selectedBadge) {
      props.onClick(badgeId);

      if (props.onValueChange) {
        props.onValueChange(selectedBadge.text);
        const jCode = getItemFromLocalStorage(
          `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
        );
        apiWorkerManager.enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id ?? "",
          order_id: singleMedicalPaOrder?.order_id ?? "",
          org_id: getOrgIdForFetchExternalWorklist() || "",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          event_name:
            EventsName.AUTH_STATUS_DROPDOWN_SELECTED +
            " (" +
            jCode?.[props.rowIndex ?? 0] +
            " - " +
            selectedBadge.text +
            ")",
          additional_data: {
            screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
            old_value: props.selectedBadge?.text ?? "",
            new_value: selectedBadge.text,
            payer_name: payerName,
            j_code: jCode?.[props.rowIndex ?? 0],
            drugs:
              singleMedicalPaOrder?.hpcs_codes
                ?.map((hpcs) => hpcs.description ?? "")
                .join(",") ?? "",
          },
        });
      }

      if (
        props.onTableRowUpdate &&
        props.fieldKey &&
        typeof props.rowIndex === "number"
      ) {
        const updatedBadgeValue = {
          text: selectedBadge.text,
          color: selectedBadge.textColor,
          bgColor: selectedBadge.bgColor,
          currentStatusId: selectedBadge.id,
        };
        props.onTableRowUpdate(
          props.rowIndex,
          props.fieldKey,
          updatedBadgeValue,
        );
      }
    }
  };

  return (
    <div className="selectable-badge">
      <div className="selectable-badge--icon">
        <BadgeDropdown
          selectedBadge={props.selectedBadge}
          badgeList={props.badgeList}
          onClick={handleBadgeSelection}
          shouldUsePortal={props.shouldUsePortal}
          onDefaultBadgeClick={(id) => {
            const jCode = getItemFromLocalStorage(
              `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
            );
            apiWorkerManager.enqueueAndProcess({
              patient_id: singleMedicalPaOrder?.patient_id ?? "",
              order_id: singleMedicalPaOrder?.order_id ?? "",
              org_id: getOrgIdForFetchExternalWorklist() || "",
              user_id: user?.id ?? "",
              user_email: user?.email ?? "",
              event_name:
                EventsName.AUTH_STATUS_DROPDOWN_CLICKED +
                " (" +
                jCode?.[props.rowIndex ?? 0] +
                ")",
              additional_data: {
                screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
                j_code: jCode?.[props.rowIndex ?? 0],
                payer_name: payerName,
              },
            });
          }}
        />
      </div>
    </div>
  );
};

export default SelectableBadge;
